// Belgisch/Vlaams getalformaat: punt voor duizendtallen, komma voor decimalen.
// Hand-rolled (niet toLocaleString) voor 100% deterministisch resultaat — sommige
// runtimes/locales lossen nl-BE op met non-breaking-space als duizend-separator.
function formatNumber(n: number, minDec: number, maxDec: number): string {
  if (n == null || isNaN(n)) return (0).toFixed(minDec).replace('.', ',');
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  // Bepaal aantal decimalen tussen min en max — pas weglatende nullen toe.
  let str = abs.toFixed(maxDec);
  if (minDec < maxDec) {
    str = str.replace(/0+$/, '').replace(/\.$/, '');
    const cur = str.includes('.') ? str.split('.')[1]!.length : 0;
    if (cur < minDec) {
      str = abs.toFixed(minDec);
    }
  }
  const [intPart, decPart] = str.split('.');
  const withThousands = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decPart ? `${sign}${withThousands},${decPart}` : `${sign}${withThousands}`;
}

/** Bedragen altijd als "1.000,00 EUR" (SPEC_calculatie, SPEC_wijzigingen). */
export function fmtEUR(n: number | null | undefined): string {
  if (n == null || isNaN(n as number)) return '0,00 EUR';
  return formatNumber(n, 2, 2) + ' EUR';
}

/** Kleurklasse voor een % arbeidskost: groen <25, geel ≤35, rood daarboven.
 *  Eén bron van waarheid voor de drempels (dashboard, projectdetail, maandoverzicht). */
export function arbeidskostTone(pct: number): string {
  // Oranje als tekst altijd in Orange Ink (warning-700): puur #F08300 haalt op wit maar 2.6:1 (DESIGN.md).
  return pct < 25 ? 'text-success' : pct <= 35 ? 'text-warning-700' : 'text-danger';
}

/** Drempel waarboven de arbeidskost als 'te hoog' gemarkeerd wordt in het maandoverzicht. */
export const ARBEIDSKOST_ALERT_PCT = 30;

/** True wanneer de % arbeidskost de alarmdrempel (>30%) overschrijdt — gemakkelijk te spotten. */
export function isArbeidskostAlert(pct: number | null | undefined): boolean {
  return pct != null && pct > ARBEIDSKOST_ALERT_PCT;
}

export function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null || isNaN(n as number)) return '0';
  return formatNumber(n, 0, dec);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-BE', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-BE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function statusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Concept',
    matched: 'Gematcht',
    approved: 'Goedgekeurd',
  };
  return map[s] ?? s;
}

export function statusChipClass(s: string): string {
  const map: Record<string, string> = {
    draft: 'chip-neutral',
    matched: 'chip-warning',
    approved: 'chip-success',
  };
  return map[s] ?? 'chip-neutral';
}

/**
 * Recotex projectnummers beginnen altijd met "P-".
 * Tijdens typen wordt automatisch geprefixt zodat de gebruiker enkel het cijferdeel hoeft in te typen.
 * Lege string blijft leeg (projectnummer is optioneel).
 */
export function normalizeProjectNummer(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (/^p-/i.test(trimmed)) {
    return 'P-' + trimmed.slice(2);
  }
  return 'P-' + trimmed.replace(/^-+/, '');
}

/** Wie iets deed, met de persoon achter een gedeeld account: "Eranda (via Calculaties)" (SPEC 27okt 1). */
export function wieLabel(naam: string | null | undefined, persoon?: string | null): string {
  const account = (naam ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  const p = (persoon ?? '').trim();
  if (!p) return account || 'Calculatietool';
  return account ? `${p} (via ${account})` : p;
}
